import { create } from 'zustand';
import { persist } from "zustand/middleware";
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { getAuthService, type LoginResponse } from '../services/auth.service';

import { type User } from '../entities/user/User';
import { License } from '../entities/user/License';
import  SessionStore  from './SessionStore';

interface IAuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasHydrated: boolean;
  license: License;
  loadingLicense: boolean;

  login: (email: string, password: string) => Promise<User | null>;
  logout: () => Promise<void> ;
  setIsLoading: (isLoading: boolean) => void;
  hydrate: () => Promise<void>;
  getLicense: () => Promise<License>;
  setUser: (user: User) => void;
}

export class AuthStore {


  static useAuthStore = create<IAuthState>()(  
    devtools(
      subscribeWithSelector(
        persist( 
          (set, get) => {
            const authService = getAuthService();

            // Synchronous re-entrancy guard for hydrate(). hasHydrated is only set
            // after the awaited getMe() below, so it can't stop a second call that
            // starts while the first is still in flight (e.g. React StrictMode's
            // double-invoked mount effect in dev, or a component remount).
            let hydrateInFlight = false;

            return {
              user: null,
              isAuthenticated: false,
              isLoading: false,
              hasHydrated: false,
              license: new License(),
              loadingLicense: false,

              hydrate: async () => {
                // Prevent double-hydrate (both already-hydrated and in-flight).
                if (get().hasHydrated || hydrateInFlight) return;
                hydrateInFlight = true;

                authService.setHydrating(true);
                set({ isLoading: true });

                try {
                  const me = await authService.getMe().catch(() => null);

                  if (me) {
                    SessionStore.useSessionStore.getState().markAuthOk();
                    set({
                      user: me,
                      isAuthenticated: true,
                      isLoading: false,
                      hasHydrated: true,
                    });
                  } else {
                    SessionStore.useSessionStore.getState().clearAuthOk();
                    SessionStore.useSessionStore.getState().hideSessionExpiredModal();
                    await authService.logout().catch(() => {});
                    set({
                      user: null,
                      isAuthenticated: false,
                      isLoading: false,
                      hasHydrated: true,
                    });
                  }
                } catch {
                  // Unexpected error - clear session state so no stale modal triggers later.
                  SessionStore.useSessionStore.getState().clearAuthOk();
                  SessionStore.useSessionStore.getState().hideSessionExpiredModal();
                  set({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                    hasHydrated: true,
                  });
                } finally {
                  authService.setHydrating(false);
                  hydrateInFlight = false;
                }
              },


              login: async (email: string, password: string): Promise<User | null> => {

                set({ isLoading: true });

                const loginRes: LoginResponse =await authService.login({ username: email, password })
                .catch((err)=>{
                  SessionStore.useSessionStore.getState().clearAuthOk();
                SessionStore.useSessionStore.getState().hideSessionExpiredModal();
                  set({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                    hasHydrated: true,
                  });
                  throw err;
                });
              
                if( loginRes.status === "success" && loginRes.user){
                  SessionStore.useSessionStore.getState().markAuthOk();

                  await authService.getLicense().then( license => {
                    set({ license });
                  }).catch(error => {
                    console.error("Failed to fetch license after login:", error);
                    set({ license: new License() }); // set to default empty license on error
                  }); 

                  
                  set({
                    user: loginRes.user,
                    isAuthenticated: true,
                    isLoading: false,
                    hasHydrated: true,
                  });
                } else {
                  SessionStore.useSessionStore.getState().clearAuthOk();
                SessionStore.useSessionStore.getState().hideSessionExpiredModal();
                  set({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                    hasHydrated: true,
                  });
                }

                return loginRes.user;
              },

              logout: async () => {
                SessionStore.useSessionStore.getState().clearAuthOk();
                SessionStore.useSessionStore.getState().hideSessionExpiredModal();
                await authService.logout();
                set({
                  user: null,
                  isAuthenticated: false,
                  isLoading: false,
                  hasHydrated: true,
                });
                
              },

              setIsLoading: (isLoading: boolean) => {
                set({ isLoading });
              },

              setUser: (user: User) => {
                set({ user });
              },

              getLicense: async (): Promise<License> => {
                set({ loadingLicense: true });
                try {
                  const license = await authService.getLicense();
                  set({ license });
                  return license;
                } catch (error) {
                  console.error("Failed to fetch license:", error);
                  set({ license: new License() });
                  return get().license;
                }finally{
                  set({ loadingLicense: false });
                }              
              },
              
            }
          },
          { 
            name: 'AuthStore',
            partialize: (state: IAuthState) => ({
              user: state.user,
              isAuthenticated: state.isAuthenticated,
            }), 
          }
        ) // persist
      ) // subscribeWithSelector  
    )// devtools    
  )//useAuthStore
} //AuthStore

export default AuthStore;

